import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { GET_TIME_SLOT_STATS } from '../../graphql/reports-queries'
import { formatEuros, formatDuration, dateRangeToUtc } from '../../lib/format'
import {
  DAY_LABELS,
  downloadReportCsv,
  SectionTitle,
  EmptyData,
  TableLoading,
  QueryError,
} from './shared'
import type { SlotSelection } from './shared'
import ProductMixPanel from './ProductMixPanel'

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * One (iso_day, hour) or (iso_day, daypart) bucket. Cells are SPARSE — a slot
 * with no orders is simply absent. Every money field and avg_prep_seconds is
 * null without the financial permission tier, even on cells with orders.
 */
interface TimeSlotCell {
  iso_day: number
  hour: number | null
  daypart: string | null
  daypart_label: string | null
  order_count: number
  revenue_cents: number | null
  /** SALES ex VAT: goods plus both fees. */
  turnover_ex_vat_cents: number | null
  /** The delivery fees inside turnover — sales, but not product. */
  delivery_fee_ex_vat_cents: number | null
  /** (turnover − delivery fee) ÷ orders: product sales per order, ex VAT. */
  avg_order_value_cents: number | null
  avg_revenue_per_occurrence_cents: number | null
  avg_prep_seconds: number | null
  prep_sample_count: number
}

interface DaypartDef {
  key: string
  label: string
  start_hour: number
  end_hour: number
}

interface TimeSlotStats {
  by_hour: TimeSlotCell[]
  by_daypart: TimeSlotCell[]
  day_occurrences: { iso_day: number; count: number }[]
  dayparts: DaypartDef[]
}

type Metric = 'revenue' | 'turnover' | 'orders' | 'avg_order' | 'per_occurrence' | 'prep'

// ─── Metric registry ──────────────────────────────────────────────────────────

const euroCompact = (v: number) => `€${Math.round(v / 100)}`

const METRICS: Record<
  Metric,
  {
    label: string
    value: (c: TimeSlotCell) => number | null
    format: (v: number) => string
    compact: (v: number) => string
  }
> = {
  revenue: {
    label: 'Revenue',
    value: (c) => c.revenue_cents,
    format: formatEuros,
    compact: euroCompact,
  },
  turnover: {
    label: 'Turnover ex VAT',
    value: (c) => c.turnover_ex_vat_cents,
    format: formatEuros,
    compact: euroCompact,
  },
  orders: {
    label: 'Orders',
    value: (c) => c.order_count,
    format: (v) => String(v),
    compact: (v) => String(v),
  },
  avg_order: {
    label: 'Avg order (ex VAT)',
    value: (c) => c.avg_order_value_cents,
    format: formatEuros,
    compact: euroCompact,
  },
  per_occurrence: {
    label: 'Revenue / day',
    value: (c) => c.avg_revenue_per_occurrence_cents,
    format: formatEuros,
    compact: euroCompact,
  },
  prep: {
    label: 'Prep time',
    value: (c) => c.avg_prep_seconds,
    format: formatDuration,
    compact: formatDuration,
  },
}

const METRIC_ORDER: Metric[] = ['revenue', 'turnover', 'orders', 'avg_order', 'per_occurrence', 'prep']

/** Below this pooled sample size a prep-time figure is shown de-emphasised. */
const LOW_PREP_SAMPLE = 5

/**
 * Roll a set of cells up into one figure. NEVER average the averages: the
 * ratio metrics are recomputed from their own numerator/denominator, and prep
 * time is a sample-weighted mean. Returns null when the figure cannot be built
 * (no permission to see money, or an empty denominator).
 */
const aggregate = (cells: TimeSlotCell[], metric: Metric, occurrenceTotal?: number): number | null => {
  switch (metric) {
    case 'orders':
      return cells.reduce((sum, c) => sum + c.order_count, 0)
    case 'revenue':
    case 'turnover': {
      let sum = 0
      let seen = false
      for (const c of cells) {
        const v = metric === 'revenue' ? c.revenue_cents : c.turnover_ex_vat_cents
        if (v != null) {
          sum += v
          seen = true
        }
      }
      return seen ? sum : null
    }
    case 'avg_order': {
      // The cell's own basis, pooled: product sales (turnover − delivery fee,
      // ex VAT) over orders.
      let productSales = 0
      let orders = 0
      let seen = false
      for (const c of cells) {
        if (c.turnover_ex_vat_cents != null) {
          productSales += c.turnover_ex_vat_cents - (c.delivery_fee_ex_vat_cents ?? 0)
          seen = true
        }
        orders += c.order_count
      }
      return seen && orders > 0 ? productSales / orders : null
    }
    case 'per_occurrence': {
      let revenue = 0
      let seen = false
      for (const c of cells) {
        if (c.revenue_cents != null) {
          revenue += c.revenue_cents
          seen = true
        }
      }
      return seen && occurrenceTotal ? revenue / occurrenceTotal : null
    }
    case 'prep': {
      let weighted = 0
      let samples = 0
      for (const c of cells) {
        if (c.avg_prep_seconds != null) {
          weighted += c.avg_prep_seconds * c.prep_sample_count
          samples += c.prep_sample_count
        }
      }
      return samples > 0 ? weighted / samples : null
    }
  }
}

/** Pooled prep sample size behind an aggregate — drives the low-sample greying. */
const prepSamples = (cells: TimeSlotCell[]): number =>
  cells.reduce((sum, c) => (c.avg_prep_seconds != null ? sum + c.prep_sample_count : sum), 0)

/** Each grid normalises its heat against its own maximum. */
const gridMax = (cells: TimeSlotCell[], metric: Metric): number =>
  cells.reduce((max, c) => {
    const v = METRICS[metric].value(c)
    return v != null && v > max ? v : max
  }, 0)

// ─── Cells ────────────────────────────────────────────────────────────────────

const HeatCell = ({
  cell,
  metric,
  max,
  isoDay,
  label,
  selected,
  onSelect,
}: {
  cell: TimeSlotCell | undefined
  metric: Metric
  max: number
  isoDay: number
  label: string
  selected: boolean
  onSelect: () => void
}) => {
  const m = METRICS[metric]
  const value = cell ? m.value(cell) : null
  const empty = value == null || value === 0
  const lowSample = metric === 'prep' && cell != null && cell.prep_sample_count < LOW_PREP_SAMPLE
  const clickable = cell != null && cell.order_count > 0

  let className = 'h-9 rounded flex items-center justify-center text-[11px] font-medium'
  let style: { backgroundColor?: string } | undefined

  if (empty) {
    className += ' bg-gray-50'
  } else if (lowSample) {
    className += ' bg-gray-100 text-gray-400'
  } else {
    // Tailwind cannot generate a per-cell intensity — inline style, as on the dashboard.
    const t = max > 0 ? (value as number) / max : 0
    style = { backgroundColor: `rgba(202, 67, 48, ${0.08 + 0.92 * t})` }
    className += t > 0.55 ? ' text-white' : ' text-gray-800'
  }
  if (selected) className += ' ring-2 ring-gray-900 ring-inset'
  if (!clickable) className += ' cursor-default'

  const title =
    `${DAY_LABELS[isoDay - 1]} ${label}: ${value != null ? m.format(value) : '—'} · ` +
    `${cell?.order_count ?? 0} orders` +
    (lowSample && value != null ? ` — low sample (${cell?.prep_sample_count} orders)` : '')

  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onSelect}
      title={title}
      className={className}
      style={style}
    >
      {empty ? '' : m.compact(value as number)}
    </button>
  )
}

const TotalCell = ({
  value,
  metric,
  greyed,
  small,
}: {
  value: number | null
  metric: Metric
  greyed: boolean
  small?: boolean
}) => {
  const text = value == null ? '—' : METRICS[metric].format(value)
  return (
    <div
      title={text}
      className={`flex items-center px-1 font-semibold truncate ${
        small ? 'text-[10px] justify-center' : 'text-xs justify-end'
      } ${greyed ? 'text-gray-400' : 'text-gray-700'}`}
    >
      {text}
    </div>
  )
}

// ─── Hour heatmap ─────────────────────────────────────────────────────────────

const HourHeatmap = ({
  byHour,
  hourMap,
  minHour,
  maxHour,
  metric,
  occurrenceByDay,
  occurrenceTotal,
  selected,
  onSelect,
}: {
  byHour: TimeSlotCell[]
  hourMap: Map<string, TimeSlotCell>
  minHour: number
  maxHour: number
  metric: Metric
  occurrenceByDay: Map<number, number>
  occurrenceTotal: number
  selected: SlotSelection | null
  onSelect: (selection: SlotSelection) => void
}) => {
  const hours: number[] = []
  for (let h = minHour; h <= maxHour; h++) hours.push(h)
  const max = gridMax(byHour, metric)

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="overflow-x-auto">
        <div
          className="grid gap-px"
          style={{
            gridTemplateColumns: `3.5rem repeat(${hours.length}, minmax(2.5rem, 1fr)) 5rem`,
          }}
        >
          {/* Header */}
          <div />
          {hours.map((h) => (
            <div key={`head-${h}`} className="text-[11px] text-gray-400 text-center">
              {h}
            </div>
          ))}
          <div className="text-[11px] text-gray-400 text-right px-1">Total</div>

          {/* Mon–Sun */}
          {DAY_LABELS.map((dayLabel, i) => {
            const isoDay = i + 1
            const dayCells = byHour.filter((c) => c.iso_day === isoDay)
            return (
              <div key={`row-${isoDay}`} className="contents">
                <div className="text-xs font-medium text-gray-500 flex items-center">{dayLabel}</div>
                {hours.map((h) => (
                  <HeatCell
                    key={`${isoDay}-${h}`}
                    cell={hourMap.get(`${isoDay}:${h}`)}
                    metric={metric}
                    max={max}
                    isoDay={isoDay}
                    label={`${h}:00–${h + 1}:00`}
                    selected={selected?.kind === 'hour' && selected.iso_day === isoDay && selected.hour === h}
                    onSelect={() => onSelect({ kind: 'hour', iso_day: isoDay, hour: h })}
                  />
                ))}
                <TotalCell
                  value={aggregate(dayCells, metric, occurrenceByDay.get(isoDay))}
                  metric={metric}
                  greyed={metric === 'prep' && prepSamples(dayCells) < LOW_PREP_SAMPLE}
                />
              </div>
            )
          })}

          {/* Column totals */}
          <div className="text-xs font-medium text-gray-500 flex items-center">All</div>
          {hours.map((h) => {
            const hourCells = byHour.filter((c) => c.hour === h)
            return (
              <TotalCell
                key={`total-${h}`}
                value={aggregate(hourCells, metric, occurrenceTotal)}
                metric={metric}
                greyed={metric === 'prep' && prepSamples(hourCells) < LOW_PREP_SAMPLE}
                small
              />
            )
          })}
          <TotalCell
            value={aggregate(byHour, metric, occurrenceTotal)}
            metric={metric}
            greyed={metric === 'prep' && prepSamples(byHour) < LOW_PREP_SAMPLE}
          />
        </div>
      </div>
    </div>
  )
}

// ─── Daypart grid ─────────────────────────────────────────────────────────────

const DaypartGrid = ({
  byDaypart,
  daypartMap,
  dayparts,
  metric,
  occurrenceByDay,
  occurrenceTotal,
  selected,
  onSelect,
}: {
  byDaypart: TimeSlotCell[]
  daypartMap: Map<string, TimeSlotCell>
  dayparts: DaypartDef[]
  metric: Metric
  occurrenceByDay: Map<number, number>
  occurrenceTotal: number
  selected: SlotSelection | null
  onSelect: (selection: SlotSelection) => void
}) => {
  const max = gridMax(byDaypart, metric)

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="overflow-x-auto">
        <div
          className="grid gap-px"
          style={{ gridTemplateColumns: `3.5rem repeat(${dayparts.length}, 1fr) 5rem` }}
        >
          {/* Header — labels come from the API, never derived from the key. */}
          <div />
          {dayparts.map((dp) => (
            <div key={`head-${dp.key}`} className="text-center">
              <div className="text-xs font-medium text-gray-600">{dp.label}</div>
              <div className="text-[10px] text-gray-400">
                {dp.start_hour}:00–{dp.end_hour}:00
              </div>
            </div>
          ))}
          <div className="text-[11px] text-gray-400 text-right px-1 flex items-end justify-end">Total</div>

          {/* Mon–Sun */}
          {DAY_LABELS.map((dayLabel, i) => {
            const isoDay = i + 1
            const dayCells = byDaypart.filter((c) => c.iso_day === isoDay)
            return (
              <div key={`row-${isoDay}`} className="contents">
                <div className="text-xs font-medium text-gray-500 flex items-center">{dayLabel}</div>
                {dayparts.map((dp) => (
                  <HeatCell
                    key={`${isoDay}-${dp.key}`}
                    cell={daypartMap.get(`${isoDay}:${dp.key}`)}
                    metric={metric}
                    max={max}
                    isoDay={isoDay}
                    label={dp.label}
                    selected={
                      selected?.kind === 'daypart' && selected.iso_day === isoDay && selected.daypart === dp.key
                    }
                    onSelect={() =>
                      onSelect({ kind: 'daypart', iso_day: isoDay, daypart: dp.key, label: dp.label })
                    }
                  />
                ))}
                <TotalCell
                  value={aggregate(dayCells, metric, occurrenceByDay.get(isoDay))}
                  metric={metric}
                  greyed={metric === 'prep' && prepSamples(dayCells) < LOW_PREP_SAMPLE}
                />
              </div>
            )
          })}

          {/* Column totals */}
          <div className="text-xs font-medium text-gray-500 flex items-center">All</div>
          {dayparts.map((dp) => {
            const cells = byDaypart.filter((c) => c.daypart === dp.key)
            return (
              <TotalCell
                key={`total-${dp.key}`}
                value={aggregate(cells, metric, occurrenceTotal)}
                metric={metric}
                greyed={metric === 'prep' && prepSamples(cells) < LOW_PREP_SAMPLE}
                small
              />
            )
          })}
          <TotalCell
            value={aggregate(byDaypart, metric, occurrenceTotal)}
            metric={metric}
            greyed={metric === 'prep' && prepSamples(byDaypart) < LOW_PREP_SAMPLE}
          />
        </div>
      </div>
    </div>
  )
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

const TimeSlotsTab = ({ startDate, endDate }: { startDate: string; endDate: string }) => {
  const [metric, setMetric] = useState<Metric>('revenue')
  const [selected, setSelected] = useState<SlotSelection | null>(null)
  const [downloading, setDownloading] = useState(false)

  const { data, loading, error, refetch } = useQuery<{ timeSlotStats: TimeSlotStats }>(
    GET_TIME_SLOT_STATS,
    {
      variables: { filters: dateRangeToUtc(startDate, endDate) },
      fetchPolicy: 'cache-and-network',
    }
  )

  const stats = data?.timeSlotStats

  // A slot picked in the old range may not exist in the new one.
  useEffect(() => setSelected(null), [startDate, endDate])

  const grid = useMemo(() => {
    const byHour = stats?.by_hour ?? []
    const byDaypart = stats?.by_daypart ?? []

    const hourMap = new Map<string, TimeSlotCell>()
    for (const c of byHour) hourMap.set(`${c.iso_day}:${c.hour}`, c)

    const daypartMap = new Map<string, TimeSlotCell>()
    for (const c of byDaypart) if (c.daypart) daypartMap.set(`${c.iso_day}:${c.daypart}`, c)

    const occurrenceByDay = new Map<number, number>()
    let occurrenceTotal = 0
    for (const d of stats?.day_occurrences ?? []) {
      occurrenceByDay.set(d.iso_day, d.count)
      occurrenceTotal += d.count
    }

    // Trimmed to the trading day, but never drops data that falls outside it.
    let minHour = 10
    let maxHour = 21
    for (const c of byHour) {
      if (c.hour == null) continue
      if (c.hour < minHour) minHour = c.hour
      if (c.hour > maxHour) maxHour = c.hour
    }

    return { byHour, byDaypart, hourMap, daypartMap, occurrenceByDay, occurrenceTotal, minHour, maxHour }
  }, [stats])

  const handleExport = async () => {
    setDownloading(true)
    try {
      await downloadReportCsv(
        `/api/reports/time-slots?start_date=${startDate}&end_date=${endDate}`,
        `time-slots-${startDate}-${endDate}.csv`
      )
    } catch {
      toast.error('Export failed. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  if (loading && !stats) return <TableLoading />
  if (error) return <QueryError message={`Failed to load time-slot report: ${error.message}`} />
  if (!stats) return null

  const hasData = stats.by_hour.length > 0 || stats.by_daypart.length > 0

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
        Orders are bucketed on their scheduled moment (Amsterdam time): ASAP orders count when
        placed, pre-orders when the food goes out. Prep time covers ASAP orders only.
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1 bg-gray-100 rounded-xl p-1">
          {METRIC_ORDER.map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                metric === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {METRICS[m].label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={handleExport}
            disabled={downloading}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-60 transition-colors whitespace-nowrap"
          >
            <ArrowDownTrayIcon className={`h-4 w-4 ${downloading ? 'animate-bounce' : ''}`} />
            {downloading ? 'Downloading…' : 'Export CSV'}
          </button>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {!hasData ? (
        <EmptyData />
      ) : (
        <>
          <div>
            <SectionTitle>Dayparts</SectionTitle>
            <DaypartGrid
              byDaypart={grid.byDaypart}
              daypartMap={grid.daypartMap}
              dayparts={stats.dayparts}
              metric={metric}
              occurrenceByDay={grid.occurrenceByDay}
              occurrenceTotal={grid.occurrenceTotal}
              selected={selected}
              onSelect={setSelected}
            />
          </div>

          <div>
            <SectionTitle>By hour</SectionTitle>
            <HourHeatmap
              byHour={grid.byHour}
              hourMap={grid.hourMap}
              minHour={grid.minHour}
              maxHour={grid.maxHour}
              metric={metric}
              occurrenceByDay={grid.occurrenceByDay}
              occurrenceTotal={grid.occurrenceTotal}
              selected={selected}
              onSelect={setSelected}
            />
          </div>

          {selected && (
            <ProductMixPanel
              selection={selected}
              startDate={startDate}
              endDate={endDate}
              onClose={() => setSelected(null)}
            />
          )}
        </>
      )}
    </div>
  )
}

export default TimeSlotsTab
